import React, { useState, useEffect, useRef } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import { SnackbarProvider } from 'notistack';
import { Switch, FormControlLabel } from "@mui/material";

import ProvenanceHome from "./views/ProvenanceHome";

import CarbonDashboards from "./CarbonDashboard";
import MarketplaceHome from "./views/MarketplaceHome";
import RegistryHome from "./views/RegistryHome";
import { WebSocketProvider } from "./context/WebSocketProvider";
import { BlockedUsersProvider } from "./context/BlockedUsersProviderIPFS";
import { KYCProvider } from "./context/KYCProvider";

import "./App.css";

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

// Header component with button navigation and theme switch
function Header({ themeMode, setThemeMode, stickySentinelRef }) {
  const navigate = useNavigate();
  const disableStickyHeader = process.env.REACT_APP_DISABLE_STICKY_HEADER === "1";
  const COMPACT_BODY_CLASS = "app-header-compact-active";
  const [hasStickyHeaderImage, setHasStickyHeaderImage] = useState(true);
  const [isCompact, setIsCompact] = useState(false);
  const lastToggleAtRef = useRef(0);
  const freezeUntilRef = useRef(0);
  const toggleTimestampsRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    const candidate = `${process.env.PUBLIC_URL}/header-sticky.png`;
    const probe = new Image();

    probe.onload = () => {
      if (!cancelled) setHasStickyHeaderImage(true);
    };

    probe.onerror = () => {
      if (!cancelled) {
        setHasStickyHeaderImage(false);
        if (process.env.NODE_ENV !== "production") {
          console.warn("[header-sticky] Missing header-sticky.png, falling back to header.png");
        }
      }
    };

    probe.src = candidate;

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (disableStickyHeader) {
      setIsCompact(false);
      return undefined;
    }

    const sentinelEl = stickySentinelRef?.current;
    if (!sentinelEl || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const ENTER_STICKY_PX = 120;
    const EXIT_STICKY_PX = 80;
    const TOGGLE_COOLDOWN_MS = 220;
    const OSCILLATION_WINDOW_MS = 1000;
    const MAX_TOGGLES_PER_WINDOW = 3;
    const DEV = process.env.NODE_ENV !== "production";

    const logDev = (reason, nextState) => {
      if (!DEV) return;
      const rect = sentinelEl.getBoundingClientRect();
      console.log("[header-sticky]", {
        reason,
        sticky: nextState,
        scrollY: Math.round(window.scrollY || window.pageYOffset || 0),
        enterThreshold: ENTER_STICKY_PX,
        exitThreshold: EXIT_STICKY_PX,
        headerHeight: window.getComputedStyle(document.documentElement).getPropertyValue("--header-normal-height"),
        sentinelTop: Math.round(rect.top),
      });
    };

    const recordToggleAndGuardOscillation = () => {
      const nowMs = Date.now();
      toggleTimestampsRef.current = toggleTimestampsRef.current.filter((ts) => nowMs - ts <= OSCILLATION_WINDOW_MS);
      toggleTimestampsRef.current.push(nowMs);

      if (toggleTimestampsRef.current.length > MAX_TOGGLES_PER_WINDOW) {
        if (DEV) {
          console.error("Sticky header oscillation detected");
        }
        freezeUntilRef.current = nowMs + 1200;
      }
    };

    const trySetCompact = (nextState, reason) => {
      const nowMs = Date.now();
      if (nowMs < freezeUntilRef.current) return;
      if (nowMs - lastToggleAtRef.current < TOGGLE_COOLDOWN_MS) return;

      setIsCompact((prev) => {
        if (prev === nextState) return prev;
        lastToggleAtRef.current = nowMs;
        recordToggleAndGuardOscillation();
        logDev(reason, nextState);
        return nextState;
      });
    };

    const enterObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const y = window.scrollY || window.pageYOffset || 0;
        if (!entry.isIntersecting && y >= ENTER_STICKY_PX) {
          trySetCompact(true, "enter-threshold");
        }
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `${ENTER_STICKY_PX}px 0px 0px 0px`,
      }
    );

    const exitObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const y = window.scrollY || window.pageYOffset || 0;
        if (entry.isIntersecting && y <= EXIT_STICKY_PX) {
          trySetCompact(false, "exit-threshold");
        }
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `${EXIT_STICKY_PX}px 0px 0px 0px`,
      }
    );

    enterObserver.observe(sentinelEl);
    exitObserver.observe(sentinelEl);

    return () => {
      enterObserver.disconnect();
      exitObserver.disconnect();
    };
  }, [stickySentinelRef, disableStickyHeader]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    if (!disableStickyHeader && isCompact) {
      document.body.classList.add(COMPACT_BODY_CLASS);
    } else {
      document.body.classList.remove(COMPACT_BODY_CLASS);
    }

    return () => {
      document.body.classList.remove(COMPACT_BODY_CLASS);
    };
  }, [disableStickyHeader, isCompact]);

  const headerBackgroundImage = disableStickyHeader || !isCompact || !hasStickyHeaderImage
    ? "header.png"
    : "header-sticky.png";

  return (
    <div className={`App-header-shell ${isCompact ? "App-header-shell--compact" : ""}`}>
      <header
        className={`App-header ${disableStickyHeader ? "App-header--no-sticky" : ""} ${isCompact ? "App-header--compact" : ""}`}
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/${headerBackgroundImage})` }}
      >
        <div className="App-header-controls">
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/")}
          style={{ fontWeight: 600, fontSize: "1rem", minWidth: 110 }}
        >
          Dashboard
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={() => navigate("/marketplace")}
          style={{ fontWeight: 600, fontSize: "1rem", minWidth: 120 }}
        >
          Marketplace
        </Button>
        <Button
          variant="contained"
          color="info"
          onClick={() => navigate("/registry")}
          style={{ fontWeight: 600, fontSize: "1rem", minWidth: 110 }}
        >
          Registry
        </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate("/provenance")}
            style={{ fontWeight: 600, fontSize: "1rem", minWidth: 130 }}
          >
            Provenance
          </Button>
        <FormControlLabel
          control={
            <Switch
              checked={themeMode === "dark"}
              onChange={e => setThemeMode(e.target.checked ? "dark" : "light")}
              color="primary"
            />
          }
          label={themeMode === "dark" ? "Dark" : "Light"}
          sx={{ marginLeft: 1 }}
        />
        </div>
      </header>
      </div>
  );
}


function App() {
  const stickySentinelRef = useRef(null);
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem("themeMode") || "light";
  });

  useEffect(() => {
    localStorage.setItem("themeMode", themeMode);
  }, [themeMode]);

  const theme = createTheme({
    palette: {
      mode: themeMode,
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        maxSnack={4}
        autoHideDuration={4000}
      >
        <Router>
          <ScrollToTopOnRouteChange />
          <BlockedUsersProvider>
            <KYCProvider>
              <WebSocketProvider>
                <div className="App-header-sentinel" ref={stickySentinelRef} aria-hidden="true" />
                <Header
                  themeMode={themeMode}
                  setThemeMode={setThemeMode}
                  stickySentinelRef={stickySentinelRef}
                />
                <Routes>
                  <Route path="/marketplace" element={<MarketplaceHome />} />
                  <Route path="/registry" element={<RegistryHome />} />
                    <Route path="/provenance" element={<ProvenanceHome />} />
                  <Route
                    path="/"
                    element={<CarbonDashboards themeMode={themeMode} />}
                  />
                </Routes>
              </WebSocketProvider>
            </KYCProvider>
          </BlockedUsersProvider>
        </Router>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;